import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { CLOUD_PROVIDERS } from '../constants/cloudProviders';

const TIER_LABELS = {
  major: 'Major Clouds',
  enterprise: 'Enterprise',
  data: 'Data Platforms',
  growth: 'Growth Clouds',
  niche: 'Specialty & Regional',
};

/**
 * CloudMultiSelect — compact dropdown + chips cloud picker.
 *
 * Props:
 *   value: string[]            — array of selected provider ids
 *   onChange: (string[]) => void
 *   mode: 'multi' | 'single'   — default 'multi'
 *   placeholder: string
 *   variant: 'light' | 'dark'
 *   className: string
 */
const CloudMultiSelect = ({
  value = [],
  onChange,
  mode = 'multi',
  placeholder = 'Select clouds…',
  variant = 'light',
  className,
  dataTestId = 'cloud-multiselect',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const isDark = variant === 'dark';

  const filteredProviders = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return CLOUD_PROVIDERS;
    return CLOUD_PROVIDERS.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.fullName.toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q)
    );
  }, [search]);

  const grouped = useMemo(() => {
    const groups = {};
    filteredProviders.forEach((p) => {
      if (!groups[p.tier]) groups[p.tier] = [];
      groups[p.tier].push(p);
    });
    return groups;
  }, [filteredProviders]);

  const selectedProviders = value
    .map((id) => CLOUD_PROVIDERS.find((p) => p.id === id))
    .filter(Boolean);

  const toggle = (id) => {
    if (mode === 'single') {
      onChange([id]);
      setOpen(false);
      return;
    }
    const next = value.includes(id) ? value.filter((v) => v !== id) : [...value, id];
    onChange(next);
  };

  const remove = (id, e) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== id));
  };

  const triggerLabel = (() => {
    if (mode === 'single') {
      if (selectedProviders.length === 0) return placeholder;
      const p = selectedProviders[0];
      return p.name;
    }
    if (selectedProviders.length === 0) return placeholder;
    if (selectedProviders.length === 1) return selectedProviders[0].name;
    return `${selectedProviders.length} clouds selected`;
  })();

  return (
    <div className={className} data-testid={dataTestId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full flex items-center justify-between gap-2 h-11 px-3 rounded-lg border transition-colors',
              isDark
                ? 'bg-slate-900 border-slate-700 hover:border-slate-500 text-white'
                : 'bg-white border-slate-200 hover:border-slate-300 text-slate-900'
            )}
            data-testid={`${dataTestId}-trigger`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {mode === 'single' && selectedProviders[0] && (() => {
                const Logo = selectedProviders[0].LogoComponent;
                return <div className="shrink-0"><Logo /></div>;
              })()}
              <span className={cn(
                'truncate text-sm',
                selectedProviders.length === 0 ? (isDark ? 'text-slate-400' : 'text-slate-400') : ''
              )}>
                {triggerLabel}
              </span>
            </div>
            <ChevronsUpDown className={cn('w-4 h-4 shrink-0 opacity-60', isDark ? 'text-slate-400' : 'text-slate-400')} />
          </button>
        </PopoverTrigger>

        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)] max-h-[420px] overflow-hidden z-[200]"
          align="start"
        >
          <div className="p-2 border-b border-slate-100 dark:border-slate-800">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
                autoFocus
                data-testid={`${dataTestId}-search`}
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-[340px] p-1">
            {Object.keys(grouped).length === 0 && (
              <p className="text-center text-xs text-slate-400 py-6">No match for "{search}"</p>
            )}
            {Object.entries(grouped).map(([tier, items]) => (
              <div key={tier} className="mb-1">
                <p className="px-2 pt-2 pb-1 text-[11px] font-medium text-slate-400 uppercase tracking-wider">
                  {TIER_LABELS[tier] || tier}
                </p>
                {items.map((p) => {
                  const isSelected = value.includes(p.id);
                  const isLive = p.live !== false; // undefined or true = live
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggle(p.id)}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left transition-colors',
                        isSelected
                          ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                      )}
                      data-testid={`${dataTestId}-option-${p.id}`}
                    >
                      <div className="shrink-0 scale-75 -mx-1"><p.LogoComponent /></div>
                      <span className="flex-1 text-sm">{p.name}</span>
                      {!isLive && (
                        <span
                          className="text-[11px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20"
                          title="Integration requestable — not yet live"
                        >
                          Soon
                        </span>
                      )}
                      {isLive && (
                        <span className="text-[11px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                          Live
                        </span>
                      )}
                      {isSelected && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Selected chips — only for multi mode */}
      {mode === 'multi' && selectedProviders.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${dataTestId}-chips`}>
          {selectedProviders.map((p) => (
            <span
              key={p.id}
              className={cn(
                'inline-flex items-center gap-1.5 pl-1.5 pr-1 py-1 rounded-full text-xs font-medium border',
                isDark
                  ? 'bg-slate-800 border-slate-700 text-slate-200'
                  : 'bg-slate-100 border-slate-200 text-slate-700'
              )}
              data-testid={`${dataTestId}-chip-${p.id}`}
            >
              <span className="scale-50 -m-2"><p.LogoComponent /></span>
              <span>{p.name}</span>
              <button
                type="button"
                onClick={(e) => remove(p.id, e)}
                className={cn(
                  'w-4 h-4 rounded-full flex items-center justify-center transition-colors',
                  isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-200'
                )}
                aria-label={`Remove ${p.name}`}
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default CloudMultiSelect;
